import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning, ProTip } from '../../components/LessonUI';

const HTMLAnchors = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Links & Navigation</div>
                <h1>Anchor Tags & Hyperlinks</h1>
                <p className="hero-lead">
                    Master the anchor elementthe foundation of the web. Learn how to create hyperlinks,
                    control their behavior, and build intuitive navigation experiences.
                </p>
            </div>

            <div className="content-section">
                <h2>The Power of Hyperlinks</h2>
                <p>
                    The anchor element (<code>&lt;a&gt;</code>) creates hyperlinksthe defining feature of the World Wide Web.
                    Links connect documents together, creating the "web" in World Wide Web.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Connectivity</h3>
                        <p>Links connect billions of web pages into a global information network</p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Navigation</h3>
                        <p>Users navigate between pages, sections, and sites using links</p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Discovery</h3>
                        <p>Search engines crawl links to discover and index web content</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>1. The Foundation: URI vs. URL</h2>
                <p>
                    While we often use the terms interchangeably, an <strong>Anchor</strong> technically uses
                    a <strong>URI (Uniform Resource Identifier)</strong>. A URL is a specific type of URI
                    that includes the location of the resource. Understanding this distinction is crucial for
                    modern web engineering, especially when dealing with custom protocols.
                </p>

                <ProTip><p><strong>Senior Engineer Tip:</strong> Think of a URI as a person's name (identity) and
                        a URL as their home address (location). In most web development contexts, the address
                        (URL) is what we use to fetch data.</p></ProTip>

                <div className="diagram-box">
                    <CodeBlock language="text" code={`[Scheme] : [//Authority] [Path] [?Query] [#Fragment]
  https  : //google.com   /search  ?q=html  #results`} />
                </div>
            </div>

            <div className="content-section">
                <h2>2. Protocol Deep Dive: Beyond the Browser</h2>
                <p>
                    The <code>href</code> attribute is surprisingly powerful. It doesn't just link to web
                    pages; it can trigger system-level actions by delegating to the operating system's
                    protocol handlers.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Protocol</th>
                                <th>Function</th>
                                <th>Mobile Behavior</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>https://</code></td>
                                <td>Standard Web Page</td>
                                <td>Opens in default browser</td>
                            </tr>
                            <tr>
                                <td><code>mailto:</code></td>
                                <td>Email Client</td>
                                <td>Opens Mail app (Outlook/Gmail)</td>
                            </tr>
                            <tr>
                                <td><code>tel:</code></td>
                                <td>Direct Dial</td>
                                <td>Prompts to initiate a call</td>
                            </tr>
                            <tr>
                                <td><code>sms:</code></td>
                                <td>Text Message</td>
                                <td>Opens Messaging app</td>
                            </tr>
                            <tr>
                                <td><code>geo:</code></td>
                                <td>Map Location</td>
                                <td>Opens Google/Apple Maps</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Advanced Protocol Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Geographic coordinates (lat, long) --&gt;
<a href="geo:37.7749,-122.4194">Find us in San Francisco</a>

<!-- Skype call --&gt;
<a href="skype:username?call">Call via Skype</a>

<!-- Direct WhatsApp message --&gt;
<a href="https://wa.me/1234567890">Message on WhatsApp</a>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>3. Basic Anchor Syntax & Anatomy</h2>
                <p>
                    The anchor element requires the <code>href</code> (hypertext reference) attribute to specify the destination.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Link Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<a href="destination-url">Link Text</a>`} />
                </div>

                <div className="diagram-box">
                    <CodeBlock language="text" code={`<a href="https://example.com">Visit Example</a>
                                
Opening  href               Link Text
  Tag   Attribute           (visible)
                                  
                             Closing Tag`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- External link --&gt;
<a href="https://www.google.com">Go to Google</a>

<!-- Link to another page on same site --&gt;
<a href="/about">About Us</a>

<!-- Link to email address --&gt;
<a href="mailto:contact@example.com">Email Us</a>

<!-- Link to phone number --&gt;
<a href="tel:+15551234567">Call Us</a>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>4. Pathing Strategy: Absolute vs. Relative</h2>
                <p>
                    Choosing between absolute and relative paths is a fundamental architectural decision.
                    It affects how your site behaves across different environments (localhost vs. staging vs. production).
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Absolute URLs</h4>
                        <p>Complete addresses including protocol and domain. Use these for external links.</p>
                        <code>https://google.com/images/logo.png</code>
                    </div>
                    <div className="concept-card">
                        <h4>Root-Relative Paths</h4>
                        <p>Starting with <code>/</code>, these point to the root of your domain. Best for internal nav.</p>
                        <code>/assets/main.css</code>
                    </div>
                    <div className="concept-card">
                        <h4>Document-Relative Paths</h4>
                        <p>Relative to the current file's location. Avoid these in large, complex sites.</p>
                        <code>../images/photo.jpg</code>
                    </div>
                </div>

                <ProTip><p><strong>DevOps Insight:</strong> Relative paths can break if you move a file to a new
                        directory or change your routing structure. Professional projects usually prefer
                        <strong>Root-Relative Paths</strong> or <strong>Environment-Aware Absolute URLs</strong>
                        generated by a build tool or framework.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>5. Types of Links: Interaction Patterns</h2>

                <h3>1. External Links & The Security layer</h3>
                <p>
                    When linking to external domains, you are essentially "sending" your user away.
                    Beyond the technical <code>target="_blank"</code>, you must manage security and SEO juice.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>`} />
                </div>

                <h3>2. Internal Links & Page SEO</h3>
                <p>
                    Internal links help search engines understand the structure of your site. Using
                    descriptive <strong>Anchor Text</strong> instead of "Click Here" is a major SEO signal.
                </p>

                <h3>3. Anchor Links (Jump Links) & Fragment Identifiers</h3>
                <p>
                    The <code>#</code> symbol defines a fragment identifier. When clicked, the browser
                    scans the DOM for an element with a matching <code>id</code> and scrolls it into view.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Smooth Scroll Implementation</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Link in Nav --&gt;
<a href="#biography">Read Biography</a>

<!-- Target Section --&gt;
<section id="biography" style="scroll-margin-top: 100px;">
    <h2>About the Author</h2>
</section>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>6. Essential Link Attributes: Beyond the Href</h2>

                <h3>1. <code>target</code> - Strategic Window Management</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Standard: Same Tab --&gt;
<a href="/dashboard" target="_self">Go to Dashboard</a>

<!-- External: New Tab --&gt;
<a href="https://partner-portal.com" target="_blank">Partner Portal</a>

<!-- Application Integration: Named Window --&gt;
<a href="/help" target="help-window">Open Help Center</a>`} />
                </div>
                <ProTip><p><strong>Senior Design Pattern: Named Targets.</strong> Using a named target like
                        <code>target="help-window"</code> instead of <code>_blank</code> ensures that multiple
                        clicks on help links always refresh the <strong>same</strong> second tab rather than
                        clogging the user's browser with 20 new tabs.</p></ProTip>

                <h3>2. The <code>rel</code> Attribute: Security & SEO Signals</h3>
                <p>
                    The <code>rel</code> attribute describes the relationship between the current document
                    and the linked resource. This is critical for both browser security and search engine
                    crawlers.
                </p>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>Category</th>
                                <th>Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>noopener</code></td>
                                <td>Security</td>
                                <td>Prevents the new page from accessing <code>window.opener</code>.</td>
                            </tr>
                            <tr>
                                <td><code>noreferrer</code></td>
                                <td>Privacy</td>
                                <td>Hides the "Referer" header from the server of the linked page.</td>
                            </tr>
                            <tr>
                                <td><code>nofollow</code></td>
                                <td>SEO</td>
                                <td>Tells Google not to pass "Link Juice" to the destination.</td>
                            </tr>
                            <tr>
                                <td><code>sponsored</code></td>
                                <td>Compliance</td>
                                <td>Identifies links that are advertisements or paid placements.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>3. The <code>download</code> Attribute: Client-Side Saving</h3>
                <p>
                    The <code>download</code> attribute allows you to force a browser to download a file
                    rather than navigating to it.
                </p>
                <ProTip><p><strong>Cross-Origin Limitation:</strong> The <code>download</code> attribute
                        only works for <strong>same-origin</strong> URLs or <code>blob:</code> and <code>data:</code>
                        schemes. If you link to an image on a different domain, it will navigate normally
                        unless the server sends a <code>Content-Disposition: attachment</code> header.</p></ProTip>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Rename file upon download --&gt;
<a href="/static/v123_report.pdf" download="Q4_Financial_Report.pdf">
    Download Q4 Report
</a>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. The Mega-Link Architecture: Clickable Cards</h2>
                <p>
                    Since HTML5, it has been legal to wrap block-level elements like <code>&lt;div&gt;</code>,
                    <code>&lt;article&gt;</code>, or <code>&lt;section&gt;</code> in an anchor tag. This is known
                    as the <strong>Mega-Link</strong> or <strong>Block-Level Link</strong> pattern.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Card Component Blueprint</span>
                    </div>
                    <CodeBlock language="html" code={`<a href="/products/quantum-processor-x" class="card-link">
    <article class="featured-card">
        <img src="/img/cpu-thumb.webp" alt="Quantum X Processor">
        <div class="card-body">
            <h3>Quantum X Series</h3>
            <p>Experience next-generation computing speeds with our latest silicon architecture.</p>
            <span class="price">$1,299.99</span>
        </div>
    </article>
</a>`} />
                </div>

                <ProTip><p><strong>Senior Strategy: Nested Links.</strong> Avoid putting buttons or other links
                        inside a Mega-Link. This creates a "nested interactive" conflict which is invalid
                        HTML and breaks screen reader navigation. If you need a secondary action, use CSS
                        to layer a button <strong>visually</strong> while keeping the DOM flat.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>8. Engineering for Accessibility (Universal Design)</h2>
                <p>
                    For many users, navigating a site is a sequence of "Tab" button presses. If your link
                    text is poor, your site becomes a maze.
                </p>

                <h3>Descriptive Link Text (The "Click Here" Anti-Pattern)</h3>
                <Warning><p><strong>Why context matters:</strong> Screen reader users often use a shortcut to
                        pull up a <strong>list of all links</strong> on the page. If that list contains 20
                        entries of "Read More" or "Click Here," the site is unusable.</p></Warning>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Poor Semantic</th>
                                <th>High-Value Semantic</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>Click <a href="...">here</a></code></td>
                                <td><code>Read the <a href="...">API Documentation</a></code></td>
                            </tr>
                            <tr>
                                <td><code><a href="...">Download</a></code></td>
                                <td><code><a href="...">Download Q3 Report (PDF, 4MB)</a></code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>Communicating Link Behavior</h3>
                <p>
                    If a link opens in a new tab or triggers a download, you <strong>must</strong>
                    communicate this to the user.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Announcing New Tabs to Screen Readers --&gt;
<a href="https://external.com" target="_blank" rel="noopener">
    Visit External Site
    <span class="sr-only">(opens in a new tab)</span>
</a>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>9. Premium Styling: The Psychology of State</h2>
                <p>
                    A link is not just a piece of text; it's a <strong>promise of a destination</strong>.
                    Visual feedback is critical to fulfilling that promise. Professional developers follow
                    the <strong>LoVe HAte</strong> order for pseudo-classes to ensure styles apply correctly.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Advanced CSS State Management</span>
                    </div>
                    <CodeBlock language="css" code={`/* 1. Link (Unvisited) */
a:link {
    color: var(--primary-color);
    text-decoration: none;
    transition: all 0.3s ease;
}

/* 2. Visited (User History) */
a:visited {
    color: var(--secondary-color);
}

/* 3. Hover (Micro-interaction) */
a:hover {
    color: var(--accent-color);
    text-decoration: underline;
}

/* 4. Active (Moment of Click) */
a:active {
    transform: translateY(1px);
}

/* 5. Focus (Keyboard Engine) */
a:focus-visible {
    outline: 3px solid var(--focus-ring);
    outline-offset: 4px;
    border-radius: 2px;
}`} />
                </div>

                <ProTip><p><strong>Senior UX Detail: The Visited State.</strong> Many modern "clean" designs
                        remove the <code>:visited</code> style. However, for content-rich sites (like
                        documentation or blogs), the visited state is a critical <strong>Cognitive Aid</strong>
                        that prevents users from clicking the same link twice unconsciously.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Architectural Navigation Patterns</h2>
                <p>
                    Anchor tags are the building blocks of <strong>Information Architecture</strong>.
                    How you organize your links determines the "flow" of your application.
                </p>

                <h3>Breadcrumb Navigation (The 'Where Am I?' pattern)</h3>
                <p>
                    Breadcrumbs use a ordered list of links to show the user's location in the site hierarchy.
                    They are essential for deep-nested sites to prevent users from getting lost.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<nav aria-label="Breadcrumb" class="breadcrumbs">
    <ol>
        <li><a href="/">Home</a></li>
        <li><a href="/products">Products</a></li>
        <li><a href="/products/hardware">Hardware</a></li>
        <li aria-current="page">I/O Controllers</li>
    </ol>
</nav>`} />
                </div>

                <h3>TOC (Table of Contents) Jump Links</h3>
                <p>
                    For long-form technical content, a sidebar of internal anchor links (jump links)
                    is a Super-Premium feature that allows users to scan and navigate quickly.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Sticky Sidebar Nav --&gt;
<aside class="toc">
    <h4>On this page</h4>
    <nav>
        <a href="#introduction">1. Introduction</a>
        <a href="#core-mechanics">2. Core Mechanics</a>
        <a href="#advanced-usage">3. Advanced Usage</a>
        <a href="#troubleshooting">4. Troubleshooting</a>
    </nav>
</aside>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Link Patterns</h2>

                <h3>Navigation Menu</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<nav>
    <ul>
        <li><a href="/" aria-current="page">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>`} />
                </div>

                <h3>Breadcrumb Navigation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<nav aria-label="Breadcrumb">
    <ol>
        <li><a href="/">Home</a></li>
        <li><a href="/blog">Blog</a></li>
        <li><a href="/blog/html">HTML</a></li>
        <li aria-current="page">Anchor Tags</li>
    </ol>
</nav>`} />
                </div>

                <h3>Social Media Links</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="social-links">
    <a href="https://twitter.com/username" target="_blank" rel="noopener" aria-label="Twitter">
        <svg><!-- Twitter icon --&gt;</svg>
    </a>
    <a href="https://facebook.com/username" target="_blank" rel="noopener" aria-label="Facebook">
        <svg><!-- Facebook icon --&gt;</svg>
    </a>
    <a href="https://linkedin.com/in/username" target="_blank" rel="noopener" aria-label="LinkedIn">
        <svg><!-- LinkedIn icon --&gt;</svg>
    </a>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>11. Security Deep-Dive: The <code>window.opener</code> Attack</h2>
                <p>
                    Most developers add <code>rel="noopener"</code> because they are told to, but few
                    understand the specific vulnerability it prevents. This is known as
                    <strong>Tab-nabbing</strong> or a <strong>Reverse Tab-napping</strong> attack.
                </p>
                <ProTip><p><strong>The exploit:</strong> When you open a link in a new tab via <code>target="_blank"</code>,
                        the new page gains access to <code>window.opener</code>. A malicious site can then use
                        JavaScript to change the <strong>original</strong> tab's URL (e.g., redirecting the
                        user to a fake login page while they are busy in the new tab).</p></ProTip>
                <div className="code-example">
                    <div className="code-header">
                        <span>The Defense</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Secure External Link --&gt;
<a href="https://untrusted-site.com" 
   target="_blank" 
   rel="noopener noreferrer">
    Visit Research Site
</a>`} />
                </div>
                <p>
                    By setting <code>rel="noopener"</code>, you instruct the browser to open the new
                    resource in a completely separate process, severing the link back to your application.
                </p>
            </div>

            <div className="content-section">
                <h2>12. The SEO Strategy: Links as Signals</h2>
                <p>
                    Search engines like Google use links to determine the <strong>Authority</strong> and
                    <strong>Context</strong> of your content.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Anchor Text Quality</h4>
                        <p>Google treats the text inside your <code>&lt;a&gt;</code> tag as a description
                            of the destination. Relevancy is the #1 ranking factor for links.</p>
                    </div>
                    <div className="concept-card">
                        <h4>Internal Connectivity</h4>
                        <p>A "flat" link structure with few internal connections is harder for crawlers to index.
                            A highly interconnected "web" of relative links indicates a deep resource.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>13. Thinking in Links: A Professional Summary</h2>
                <p>
                    Anchor tags are the nervous system of the web. They don't just move users from point A
                    to point B; they define the information hierarchy of the digital world.
                </p>
                <ProTip><p><strong>Senior Engineer Mindset:</strong> When building a hyperlink, ask yourself:
                        Is the destination secure? Is the text meaningful without context? Have I managed
                        the user's focus? If you answer yes to all three, you are writing production-grade HTML.</p></ProTip>
                <p className="mt-4">
                    Mastering the anchor tag is the first step toward becoming a master of
                    <strong>UX Design</strong> and <strong>Web Architecture</strong>. Every link you
                    create is a path you've built for your usermake sure it's a smooth, accessible,
                    and secure journey.
                </p>
            </div>

            <div className="content-section">
                <h2>15. Historical Context: From Gopher to Hypertext</h2>
                <p>
                    Before the web we know today, the internet was a collection of fragmented protocols.
                    The <strong>Gopher</strong> protocol was a precursor to HTTP, but it lacked the
                    ability to embed links directly within text. Tim Berners-Lee's decision to make the
                    anchor tag a "first-class citizen" of HTML is what truly birthed the
                    <strong>World Wide Web</strong>. It transformed the internet from a
                    file-transfer network into a non-linear, exploratory universe.
                </p>
            </div>

            <div className="content-section">
                <h2>16. Performance Optimization: Prefetching & Prerendering</h2>
                <p>
                    Modern browsers don't just wait for a user to click. Using the <code>rel</code>
                    attribute with <code>prefetch</code> or <code>dns-prefetch</code>, you can
                    optimize your site's perceived speed.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Resolving the IP address early --&gt;
<link rel="dns-prefetch" href="https://api.external-data.com">

<!-- Predicting the next page the user will visit --&gt;
<link rel="prefetch" href="/next-article.html">`} />
                </div>
                <p>
                    By signaling to the browser which links are likely to be clicked next, you can
                    drastically reduce "Time to First Byte" (TTFB) for subsequent navigations,
                    creating a "zero-latency" feeling for the user.
                </p>
            </div>

            <div className="content-section">
                <h2>14. Advanced: The Screen Reader "Rotor" Experience</h2>
                <p>
                    To truly master anchor tags, you must understand how power users interact with them.
                    Assistive technologies like <strong>VoiceOver</strong> (Mac/iOS) and <strong>NVDA</strong> (Windows)
                    feature a tool called the <strong>Rotor</strong> or <strong>Elements List</strong>.
                </p>
                <ProTip><p><strong>The Developer's Audit:</strong> Open your browser's dev tools and look at the
                        <strong>Accessibility Tab</strong>. You can see the "Accessible Name" calculated by
                        the browser for every link. If that name is just "link" or "(empty)", your anchor
                        strategy has failed.</p></ProTip>
                <p>
                    Users often scan this list alphabetically to find what they need. If you have
                    multiple links starting with "The...", they will all be bunched together, making
                    discovery difficult. Always place the <strong>unique keyword</strong> at the
                    beginning of your link text whenever possible.
                </p>
            </div>
        </div>
    );
};

export default HTMLAnchors;



